'use client';
import React, { useMemo } from 'react';
import './FactionCollectionStats.css';
import { useTranslations } from 'next-intl';
import type { FactionProfile } from '@wwe-portal/entities/faction.type';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';
import { tryFormatThousands } from '@wwe-portal/utils/format';

type StarRankResult = {
  totalInFaction: number;
  rank: number;
};

function FactionCollectionStats({
  faction,
}: Readonly<{ faction: FactionProfile }>) {
  const t = useTranslations();

  const starCounts = useMemo(() => {
    const counts: StarRankResult[] = [];
    for (let i = 6; i >= 1; i--) {
      counts.push({
        rank: i,
        totalInFaction: Number(faction?.[`${i}star_ss_count`] || 0),
      });
    }
    return counts;
  }, [faction]);

  return faction ? (
    <div className="relative mb-[50px]">
      <span className="mb-[30px] text-xl font-medium">
        {t('collection_stats')}
        <span className="text-text600 ml-1 text-sm font-medium">
          ({t('last_12_days')})
        </span>
      </span>
      <div className="faction-profile-collection mt-7 flex flex-col items-center rounded-[10px] py-[30px] sm:flex-wrap md:flex-row">
        {starCounts.map((item, i) => {
          return (
            <div
              key={i}
              className="faction-profile-collection-stats border-r-back200 flex w-full flex-col gap-5 border-r py-5 last:border-0 md:w-[16.6%]"
            >
              <div className="flex flex-col items-center justify-center">
                <div>
                  <div className="mb-[15px] flex gap-2 md:mb-5">
                    {item.rank}
                    <IcoMoonIcon
                      iconName="star-fill"
                      className="text-inlineTextColor11 size-5"
                    />
                  </div>
                </div>
                <span className="text-text400 m-0 text-2xl font-medium">
                  {tryFormatThousands(item.totalInFaction)}
                </span>
                <p className="text-text600 m-0 mt-[5px] p-0 text-sm">
                  {t('total_in_faction')}
                </p>
              </div>
              <hr className="faction-profile-collection-stats-hr border-back200 mx-auto my-5 border md:my-[10px] md:block" />
              <div className="flex flex-col items-center justify-center">
                <span className="faction-profile-collection-stats-text-circle text-text400 m-0 p-0 font-semibold">
                  {((item.totalInFaction * 1.0) / faction.active_member_count)
                    .toFixed(1)
                    .replace(/\.0$/, '')}
                </span>
                <p className="text-text600 m-0 mt-[10px] w-[200px] p-0 text-center text-sm">
                  {t('avg_per_member')}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : null;
}

export default FactionCollectionStats;
